import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
// Angular Chart Component
import { AgCharts } from 'ag-charts-angular';
// Chart Options Type Interface
import { AgBarSeriesOptions, AgChartOptions, AgPieSeriesOptions } from 'ag-charts-community';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { ApprovalState, TransformationResult, UserRoles } from '../../models/appraisal';
import { AppraisalDataService } from '../../shared/services/appraisal-data.service';
import { UserService } from '../../shared/services/user.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-dashboard-container',
  standalone: true,
  imports: [CommonModule, AgCharts, MatCardModule, MatButtonModule, MatIconModule],
  templateUrl: './dashboard-container.component.html',
  styleUrl: './dashboard-container.component.scss'
})
export class DashboardContainerComponent {
  public chartOptions: AgChartOptions;
  charts: Array<{ propertyType: string; address: string; options: AgChartOptions }> = [];
  dashboardData: any;
  propertyTypeChart?: AgChartOptions;
  officeCityChart?: AgChartOptions;
  yearBuiltChart?: AgChartOptions;
  vacancyChart?: AgChartOptions;
  capRateChart?: AgChartOptions;

  statusStats = {
    total: 0,
    readyForClient: 0,
    inReview: 0,
    notApproved: 0,
    approved: 0,
  };

  approvalCounts = {
    approved: 0,
    pending: 0,
    rejected: 0,
  };
  
  userData: any;

  private computeStatusTiles(data: any[]): void {
    this.approvalCounts = { approved: 0, pending: 0, rejected: 0 };
  
    data.forEach((item) => {
      const state = item.latestReview?.status;
  
      switch (state) {
        case ApprovalState.Approved:
          this.approvalCounts.approved++;
          break;
        case ApprovalState.PendingAppraiserReview:
        case ApprovalState.PendingPortfolioReview:
        case ApprovalState.PendingQualityReview:
          this.approvalCounts.pending++;
          break;
        case ApprovalState.Rejected:
          this.approvalCounts.rejected++;
          break;
      }
    });
  }

  constructor(private http: HttpClient, private userService: UserService,  private appraisalDataService: AppraisalDataService,
    public router: Router
  ) {
    this.chartOptions = {
      // Data: Data to be displayed in the chart
      data: [
        { month: 'Jan', avgTemp: 2.3, iceCreamSales: 162000 },
        { month: 'Mar', avgTemp: 6.3, iceCreamSales: 302000 },
        { month: 'May', avgTemp: 16.2, iceCreamSales: 800000 },
        { month: 'Jul', avgTemp: 22.8, iceCreamSales: 1254000 },
        { month: 'Sep', avgTemp: 14.5, iceCreamSales: 950000 },
        { month: 'Nov', avgTemp: 8.9, iceCreamSales: 200000 },
      ],
      // Series: Defines which chart type and data to use
      series: [
        {
          type: "bar",
          xKey: "month",
          yKey: "iceCreamSales",
        } as AgBarSeriesOptions,
      ],
    };
  }

  ngOnInit(): void {
    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (this.userData.roles.includes('Ratings')) {
        this.router.navigate(['/404']); 
      }
      if (this.userData)
        this.fetchData();
    });
  }

  fetchData(): void {
    const apiUrl = environment.appraisalApiConfig.baseUrl + '/by-email-and-role'; // API endpoint
    const queryParams = {
      email: this.userData.email,
      role: UserRoles[this.userData.currentRole as keyof typeof UserRoles]
    };
  
    this.http.get<TransformationResult<any>[]>(apiUrl, { params: queryParams }).subscribe(
      (results) => {
        // Log or alert if any of the transformation results are invalid.
        // const invalidResults = results.filter(item => !item.isValid);
        // if (invalidResults.length > 0) {
        //   console.error('Validation errors while fetching dashboard data:', invalidResults.map(item => item.validationErrors));
        //   alert('Some items could not be fetched properly:\n' +
        //     invalidResults.map(item =>
        //       item.validationErrors.map(err => err.message).join('; ')
        //     ).join('\n'));
        // }
        // Unwrap the underlying values from each transformation result.
        const data = results.map(item => item.value);
  
        this.dashboardData = data;
        this.processData(data);
        this.appraisalDataService.setAllRowsData(data);
        this.computeStatusTiles(data);
      },
      (error) => {
        console.error('Error fetching data:', error);
      }
    );
  }  

  processData(data: any[]): void {
    this.charts = data.map((item) => ({
      propertyType: item.propertyType || 'Unknown Type',
      address: item.address || 'Unknown Address',
      options: {
        data: [
          { year: 'Year 1', value: parseFloat(item.year1) || 0 },
          { year: 'Year 2', value: parseFloat(item.year2) || 0 },
          { year: 'Year 3', value: parseFloat(item.year3) || 0 },
          { year: 'Year 4', value: parseFloat(item.year4) || 0 },
          { year: 'Year 5', value: parseFloat(item.year5) || 0 },
        ],
        series: [
          {
            type: 'bar',
            xKey: 'year',
            yKey: 'value',
            yName: 'Value ($)',
          } as AgBarSeriesOptions,
        ],
      },
    }));

    this.propertyTypeChart = {
      data: this.getCountsByField(data, 'propertyTypeAndTenancy.propertyType'),
      series: [
        {
          type: 'bar',
          xKey: 'label',
          yKey: 'count',
          yName: 'Appraisals',
          fill: '#AAACAC',
          stroke: '#80BEAC'
        } as AgBarSeriesOptions
      ]
    };
    
    this.officeCityChart = {
      title: { text: 'Office Appraisals by City' },
      data: this.getCountsByField(
        data.filter(d => d.propertyTypeAndTenancy.propertyType === 'Office'),
        'subjectPropertyLocation.city'
      ),
      series: [
        {
          type: 'pie',
          angleKey: 'count',
          labelKey: 'label',
          calloutLabelKey: 'label',
          sectorLabelKey: 'count',
          innerRadiusOffset: -50,
          fills: ['#AAACAC'],
          strokes: ['#FFFFFF'],
          tooltip: {
            renderer: ({ datum }) => ({
              content: `${datum.label}: ${datum.count}`,
            }),
          },
        } as AgPieSeriesOptions
      ],
      legend: { position: 'right', enabled: true }
    };    

    this.yearBuiltChart = {
      title: { text: 'Year Built Distribution (Office Properties)' },
      data: this.getCountsByField(
        data.filter(d =>
          d.propertyTypeAndTenancy?.propertyType === 'Office' &&
          d.improvementInformation?.yearBuilt
        ),
        'improvementInformation.yearBuilt'
      ),
      series: [
        {
          type: 'bar',
          direction: 'vertical',
          xKey: 'label',
          yKey: 'count',
          yName: 'Number of Properties',
          fill: '#AAACAC',
          stroke: '#80C9D6',
          tooltip: {
            renderer: ({ datum }) => ({
              content: `Year ${datum.label}: ${datum.count} properties`,
            }),
          },
        } as AgBarSeriesOptions
      ],
      axes: [
        { type: 'category', position: 'bottom', title: { text: 'Year Built' } },
        { type: 'number', position: 'left', title: { text: 'Count' } },
      ],
      legend: { enabled: false }
    };    
    
    this.vacancyChart = {
      title: { text: 'Vacancy Distribution (Office)' },
      data: this.getCountsByField(
        data.filter(d => d.propertyTypeAndTenancy.propertyType === 'Office'),
        'improvementInformation.vacancy'
      ),
      series: [
        {
          type: 'pie',
          angleKey: 'count',
          labelKey: 'label',
          calloutLabelKey: 'label',
          sectorLabelKey: 'count',
          innerRadiusOffset: -50,
          fills: ['#AAACAC'],
          strokes: ['#FFFFFF'],
          tooltip: {
            renderer: ({ datum }) => ({
              content: `${datum.label}: ${datum.count}`,
            }),
          },
        } as AgPieSeriesOptions
      ],
      legend: { position: 'bottom', enabled: true }
    }; 

    this.capRateChart = {
      title: { text: 'Capitalization Rate by Property' },
      data: data
        .filter(d => d.cppib?.capitalizationRate)
        .map((d, idx) => ({
          label: d.subjectPropertyLocation?.propertyName || `Property ${idx + 1}`,
          capRate: parseFloat(d.cppib.capitalizationRate),
        })),
      series: [
        {
          type: 'bar',
          direction: 'vertical',
          xKey: 'label',
          yKey: 'capRate',
          yName: 'Cap Rate (%)',
          fill: '#AAACAC',
          stroke: '#AAACAC',
          tooltip: {
            renderer: ({ datum }) => ({
              content: `${datum.label}: ${datum.capRate.toFixed(2)}%`,
            }),
          },
        } as AgBarSeriesOptions
      ],
      axes: [
        { type: 'category', position: 'bottom', title: { text: 'Property' } },
        { type: 'number', position: 'left', title: { text: 'Cap Rate (%)' } },
      ],
      legend: { enabled: false }
    };
    
     
  }

  getCountsByField(data: any[], fieldPath: string): { label: string, count: number }[] {
    const field = (obj: any, path: string): any =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);
  
    const map = new Map<string, number>();
  
    data.forEach(item => {
      const key = field(item, fieldPath) || 'Unknown';
      map.set(key, (map.get(key) || 0) + 1);
    });
  
    return Array.from(map.entries()).map(([label, count]) => ({ label, count }));
  }
  
}
